import React from 'react';
import { v4 as uuid } from 'uuid';
import { findIndex , get } from 'lodash/fp';
import { Field, FieldArray, FieldInputProps, FormikProps } from 'formik';
import { Box, Button, FormHelperText, Icon, IconButton, InputBase, InputLabel, withStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useTranslation } from 'react-i18next';
import { CreateCategoryValues } from './types';

const StyledInput = withStyles(theme => ({
    input: {
        borderRadius: 10,
        position: 'relative',
        backgroundColor: theme.palette.common.white,
        border: '1px solid #DCDCDC',
        padding: '10px 12px',
    },
}))(InputBase);

interface InputProps extends FieldInputProps<CreateCategoryValues> {
    label: string;
    handleRemove: () => void;
    error: boolean;
    helperText?: string;
}

interface ChildCategoriesInputProps {
    name: string;
}

const Input: React.FC<InputProps> = ({ handleRemove, label, error, name, helperText, ...other }) => {
    return (
        <Box flex={1} mb={1.5}>
            <InputLabel shrink htmlFor={name}>
                {label}
            </InputLabel>
            <Box display='flex' alignItems='flex-start'>
                <Box flex={1}>
                    <StyledInput fullWidth name={name} autoFocus {...other} />
                    <FormHelperText error={error}>{helperText}</FormHelperText>
                </Box>
                <IconButton color='inherit' onClick={handleRemove}>
                    <Icon>close</Icon>
                </IconButton>
            </Box>
        </Box>
    );
};

export const ChildCategoriesInput: React.FC<ChildCategoriesInputProps> = ({ name }) => {
    const { t } = useTranslation();
    return (
        <FieldArray
            name={name}
            render={
                ({ remove, push, form }) => {
                    const addChildCategory = () => push({ name: '', id: uuid() });
                    const {
                            values: { childCategories },
                            errors: { childCategories: errors },
                            touched: { childCategories: touched },
                        } = form as FormikProps<CreateCategoryValues>;
                    return (
                        <>
                            {childCategories.map((childCategory, index) => {
                                const childIndex = findIndex({ id: childCategory.id }, childCategories);
                                const removeChildCategory = () => remove(childIndex);
                                const isTouched = !!(touched && touched[index]?.name);
                                const error = !!(errors && isTouched && errors[index]);
                                const helperText: string | undefined = get(`${index}.name`, errors);
                                return (
                                    <Field
                                        as={Input}
                                        error={error}
                                        helperText={helperText}
                                        handleRemove={() => removeChildCategory()}
                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                            if (e.keyCode === 13) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                addChildCategory();
                                            }
                                        }}
                                        key={childCategory.id}
                                        name={`childCategories.${childIndex}.name`}
                                        label={t('Subcategory name')}
                                    />
                                );
                            })}
                            <Button
                                color='primary'
                                variant='outlined'
                                startIcon={<AddIcon />}
                                onClick={addChildCategory}
                            >
                                {t('Subcategory')}
                            </Button>
                        </>
                    );
                }
            }
        />
    );
};
